/**
 * Act3TrialReveal — 考验揭示卡片
 */
import React from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import type { TrialCard } from '@shared/types/cards';

interface Act3TrialRevealProps {
  trial: TrialCard;
  weekNumber: number;
  onConfirm: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  '距离': 'from-blue-50 to-cyan-50 border-blue-200',
  '经济': 'from-amber-50 to-yellow-50 border-amber-200',
  '信任': 'from-red-50 to-pink-50 border-red-200',
  '社交': 'from-green-50 to-teal-50 border-green-200',
  '价值观': 'from-purple-50 to-indigo-50 border-purple-200',
  '突发': 'from-orange-50 to-red-50 border-orange-200',
};

export const Act3TrialReveal: React.FC<Act3TrialRevealProps> = ({ trial, weekNumber, onConfirm }) => {
  const colorClass = CATEGORY_COLORS[trial.category] || 'from-gray-50 to-slate-50 border-gray-200';

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className={`max-w-sm w-full bg-gradient-to-br ${colorClass} border-2 rounded-2xl p-6 shadow-lg`}>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={20} className="text-red-500" />
          <span className="text-[10px] text-[#9CA3AF]">第 {weekNumber} 周考验</span>
        </div>

        <h2 className="text-xl font-bold text-[#2D2D2D] mb-2">{trial.name}</h2>
        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-white/60 text-[#6B7280] font-medium mb-3">
          {trial.category} · 持续 {trial.duration} 周
        </span>

        <p className="text-sm text-[#6B5B4F] mb-4 leading-relaxed">{trial.description}</p>

        <div className="bg-white/50 rounded-xl p-3 mb-4">
          <span className="text-[10px] text-[#9CA3AF] block mb-1">提示</span>
          <p className="text-xs text-[#6B5B4F] italic">{trial.hint}</p>
        </div>

        {/* Auto damage preview */}
        <div className="bg-red-50/50 rounded-xl p-3 mb-6">
          <span className="text-[10px] text-red-400 block mb-1">每周自动损耗</span>
          <div className="flex gap-3 text-[10px] text-red-500">
            <span>承诺 {trial.autoDamage.commitment}</span>
            <span>精力 {trial.autoDamage.energy}</span>
            <span>稳定 {trial.autoDamage.stability}</span>
          </div>
        </div>

        <button
          onClick={onConfirm}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
        >
          选择应对策略 <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Act3TrialReveal;
